import React from 'react';
import { Key, Laptop, Globe, Layers, Database, ChevronRight } from 'lucide-react';
import { CISA_PILLARS, CISA_FUNCTIONS } from '../../../config/cisaMaturityModel';

/**
 * Icon map for pillars
 */
const PILLAR_ICONS = {
  IDENTITY: Key,
  DEVICES: Laptop,
  NETWORKS: Globe,
  APPLICATIONS: Layers,
  DATA: Database
};

/**
 * Maturity level info for display
 */
const MATURITY_LEVELS = {
  TRADITIONAL: { label: 'Traditional', color: 'var(--zt-traditional, #6b7280)' },
  INITIAL: { label: 'Initial', color: 'var(--zt-initial, #eab308)' },
  ADVANCED: { label: 'Advanced', color: 'var(--zt-advanced, #3b82f6)' },
  OPTIMAL: { label: 'Optimal', color: 'var(--zt-optimal, #10b981)' }
};

/**
 * PillarDrilldown Component
 *
 * Displays the functions within a selected pillar.
 * Shows current and target maturity levels for each function.
 */
function PillarDrilldown({
  pillarCode,
  assessments = {},
  targetLevels = {},
  onSelectFunction
}) {
  const pillar = CISA_PILLARS.find(p => p.code === pillarCode);
  const functions = CISA_FUNCTIONS[pillarCode] || [];
  const Icon = PILLAR_ICONS[pillarCode] || Layers;

  if (!pillar) {
    return (
      <div className="ztj-empty-state">
        <Layers size={48} />
        <p>Pillar not found</p>
      </div>
    );
  }

  return (
    <div className="ztj-drilldown">
      <div
        className="ztj-drilldown-header"
        style={{ '--pillar-color': pillar.color }}
      >
        <div
          className="ztj-drilldown-icon"
          style={{
            background: `${pillar.color}15`,
            color: pillar.color
          }}
        >
          <Icon size={28} />
        </div>
        <div className="ztj-drilldown-title">
          <h2>{pillar.name} Pillar</h2>
          <p>Select a function to explore maturity progression</p>
        </div>
      </div>

      <div className="ztj-functions-grid">
        {functions.map(fn => {
          const currentLevel = assessments[fn.code] || 'TRADITIONAL';
          const targetLevel = targetLevels[fn.code];
          const currentInfo = MATURITY_LEVELS[currentLevel];
          const targetInfo = targetLevel ? MATURITY_LEVELS[targetLevel] : null;

          // Calculate progress percentage (0-100)
          const levelOrder = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
          const currentIndex = levelOrder.indexOf(currentLevel);
          const progressPercent = (currentIndex / 3) * 100;

          return (
            <button
              key={fn.code}
              type="button"
              className="ztj-function-card"
              onClick={() => onSelectFunction(fn.code)}
            >
              <div className="ztj-function-card-header">
                <h3 className="ztj-function-name">{fn.name}</h3>
                <ChevronRight size={18} style={{ color: 'var(--color-text-subtle)' }} />
              </div>

              <div className="ztj-function-levels">
                <span
                  className="ztj-level-badge ztj-level-badge--current"
                  style={{
                    background: `${currentInfo.color}15`,
                    color: currentInfo.color
                  }}
                >
                  Current: {currentInfo.label}
                </span>
                {targetInfo && targetLevel !== currentLevel && (
                  <span className="ztj-level-badge ztj-level-badge--target">
                    Target: {targetInfo.label}
                  </span>
                )}
              </div>

              <div className="ztj-function-progress">
                <div className="ztj-function-progress-bar">
                  <div
                    className="ztj-function-progress-fill"
                    style={{
                      width: `${progressPercent}%`,
                      background: currentInfo.color
                    }}
                  />
                </div>
                <span className="ztj-function-progress-label">
                  {currentIndex + 1}/4
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default PillarDrilldown;
